import React from 'react';
import { api } from '../lib/api';
import { Badge, Btn } from './index';
import { formatInTz, DEFAULT_TIMEZONE } from '../lib/timezone';
import { palette as T, type } from '../design';

// Admin's applications inbox (AD-4, AD-5). The server sends them newest first, 50 per page.
// Invite hands the application to the dashboard, which opens the create-instructor form filled
// in from it; the dashboard marks it invited only after that form saves. Decline asks to confirm,
// then marks it declined here and the server emails the applicant.

const ADMIN_TZ = DEFAULT_TIMEZONE;
const DATE_ONLY = { month: 'short', day: 'numeric', year: 'numeric' };

// Mirror of APPLICATION_STATUS in backend/src/lib/constants.js. The words show in the UI verbatim.
export const APPLICATION_STATUS = { NEW: 'new', INVITED: 'invited', DECLINED: 'declined' };
const STATUS_BADGE = { [APPLICATION_STATUS.NEW]: 'gold', [APPLICATION_STATUS.INVITED]: 'sage' };

export const APPLICATIONS_LOAD_FAILED = 'We could not load applications. Check your connection, then try again.';
export const DECLINE_FAILED = 'We could not decline this application. Check your connection, then try again.';

const FONT = "'Plus Jakarta Sans', sans-serif";
const textStyle = { fontFamily: FONT, fontSize: type.body, color: T.walnut, margin: 0, overflowWrap: 'anywhere' };
const noteStyle = { fontFamily: FONT, fontSize: type.body, color: T.driftwood, margin: 0 };

// The create-instructor form fields an application fills in. An application has no display
// name, so the applicant's name stands in until admin edits it.
export function instructorFormFromApplication(application) {
  return {
    name: application.name || '',
    displayName: application.name || '',
    email: application.email || '',
    bio: application.about || '',
  };
}

// The message admin reads when a declined applicant was not emailed (AD-5.2 on the server).
// The answer is saved; the applicant still needs to hear it, so admin writes to them.
export function declineEmailFailedMessage(application) {
  return `You declined ${application.name}'s application, but the email to them did not send. `
    + `Write to ${application.email} from the support address. The Error Log has the details.`;
}

// Only web links become links: the applicant typed this address.
function isWebLink(url) {
  return /^https?:\/\//i.test(url || '');
}

function DeclineApplicationConfirm({ name, busy, error, onConfirm, onCancel }) {
  const headingId = React.useId();
  const groupRef = React.useRef(null);
  // The Decline button that opened this step is gone, so keyboard focus moves to the first
  // button here (Decline application) instead of falling to the page.
  React.useEffect(() => {
    groupRef.current?.querySelector('button')?.focus();
  }, []);
  return (
    <div ref={groupRef} role="group" aria-labelledby={headingId}
      style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 380, padding: 12, borderRadius: 12, border: '1px solid ' + T.border, background: T.ivory, boxSizing: 'border-box', marginTop: 10 }}>
      <p id={headingId} style={{ fontFamily: FONT, fontSize: type.body, fontWeight: 600, color: T.espresso, margin: 0 }}>Decline this application?</p>
      <p style={{ ...noteStyle, fontSize: type.label, lineHeight: 1.5 }}>
        {name} gets an email that you declined. You cannot change this answer later.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn size="sm" onClick={onConfirm} disabled={busy}>{busy ? 'Declining…' : 'Decline application'}</Btn>
        <Btn size="sm" variant="subtle" onClick={onCancel} disabled={busy}>Keep application</Btn>
      </div>
      {error && <span role="alert" style={{ fontFamily: FONT, fontSize: type.label, color: T.errorRed, lineHeight: 1.5 }}>{error}</span>}
    </div>
  );
}

function ApplicationRow({ application, onInvite, onDeclined }) {
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Set by Keep application: focus goes back to Decline once it is on screen again.
  const [refocusDecline, setRefocusDecline] = React.useState(false);
  const declineRef = React.useRef(null);
  const a = application;
  const isNew = a.status === APPLICATION_STATUS.NEW;

  React.useEffect(() => {
    if (!refocusDecline || confirming) return;
    declineRef.current?.querySelector('button')?.focus();
    setRefocusDecline(false);
  }, [refocusDecline, confirming]);

  const keepApplication = () => {
    setConfirming(false);
    setError(null);
    setRefocusDecline(true);
  };

  const decline = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.patch(`/admin/applications/${a.id}`, { status: APPLICATION_STATUS.DECLINED });
      setConfirming(false);
      onDeclined(res.application, res.emailDelivered);
    } catch (err) {
      console.error('Application decline failed:', err);
      setError(err?.data?.message || DECLINE_FAILED);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li aria-label={a.name} style={{ listStyle: 'none', padding: '16px 0', borderBottom: '1px solid ' + T.border }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <p style={{ fontFamily: FONT, fontWeight: 600, fontSize: type.body, color: T.espresso, margin: 0 }}>{a.name}</p>
          <p style={textStyle}>{a.email} · {a.language} · {a.yearsTeaching}</p>
          <p style={{ ...noteStyle, fontSize: type.label }}>Applied {formatInTz(a.createdAt, ADMIN_TZ, DATE_ONLY)}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Badge variant={STATUS_BADGE[a.status] || 'subtle'}>{a.status}</Badge>
          {a.answeredAt && <span style={{ ...noteStyle, fontSize: type.label }}>Answered {formatInTz(a.answeredAt, ADMIN_TZ, DATE_ONLY)}</span>}
        </div>
        {isNew && !confirming && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Btn size="sm" onClick={() => onInvite(a)}>Invite</Btn>
            <span ref={declineRef}>
              <Btn size="sm" variant="subtle" onClick={() => { setError(null); setConfirming(true); }}>Decline</Btn>
            </span>
          </div>
        )}
      </div>
      {confirming && (
        <DeclineApplicationConfirm name={a.name} busy={busy} error={error}
          onConfirm={decline} onCancel={keepApplication} />
      )}
      <details style={{ marginTop: 8 }}>
        <summary style={{ fontFamily: FONT, fontSize: type.label, fontWeight: 600, color: T.terracotta, cursor: 'pointer' }}>Read the application</summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          <p style={{ ...textStyle, whiteSpace: 'pre-wrap' }}>{a.about}</p>
          {a.certifications && <p style={textStyle}>Certifications: {a.certifications}</p>}
          {isWebLink(a.introVideoUrl) && (
            <a href={a.introVideoUrl} target="_blank" rel="noopener noreferrer" style={{ ...textStyle, color: T.terracotta }}>Intro video</a>
          )}
        </div>
      </details>
    </li>
  );
}

export function AdminApplicationsPanel({ onInvite }) {
  const [page, setPage] = React.useState(1);
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [warning, setWarning] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get('/admin/applications?page=' + page)
      .then((res) => { if (!cancelled) setResult(res); })
      .catch((err) => {
        console.error('Admin applications fetch failed:', err);
        if (!cancelled) setError(err?.data?.message || APPLICATIONS_LOAD_FAILED);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, reloadKey]);

  // The server's reply is the saved row: status and answered date come from it, not from here.
  const handleDeclined = (updated, emailDelivered) => {
    setResult((prev) => ({
      ...prev,
      applications: prev.applications.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
    }));
    setWarning(emailDelivered === false ? declineEmailFailedMessage(updated) : null);
  };

  const applications = result?.applications || [];
  const total = result?.total || 0;
  const pageSize = result?.pageSize || 50;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <p style={{ ...noteStyle, marginBottom: 12 }}>Newest first. Dates are in {ADMIN_TZ} time.</p>

      {warning && (
        <div role="alert" style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.errorRed, padding: 16, marginBottom: 16 }}>
          <p style={{ ...noteStyle, color: T.errorRed }}>{warning}</p>
        </div>
      )}

      {error && (
        <div role="alert" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, background: T.white, borderRadius: 16, border: '1px solid ' + T.errorRed, padding: 16, marginBottom: 16 }}>
          <p style={{ ...noteStyle, color: T.errorRed, flex: '1 1 200px' }}>{error}</p>
          <Btn size="sm" variant="ghost" onClick={() => setReloadKey((k) => k + 1)}>Try again</Btn>
        </div>
      )}

      <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '4px 20px' }}>
        <ul aria-label="Applications" style={{ margin: 0, padding: 0 }}>
          {applications.map((a) => (
            <ApplicationRow key={a.id} application={a} onInvite={onInvite} onDeclined={handleDeclined} />
          ))}
        </ul>
        {!loading && !error && applications.length === 0 && (
          <p style={{ ...noteStyle, fontSize: type.body, textAlign: 'center', padding: '36px 0' }}>No applications yet. New ones show here when someone applies to teach.</p>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 16 }}>
        <p style={noteStyle} aria-live="polite">
          {loading ? 'Loading applications…' : total > 0 ? `${total} application${total === 1 ? '' : 's'}` : ''}
        </p>
        {total > pageSize && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Btn size="sm" variant="subtle" onClick={() => setPage((p) => p - 1)} disabled={loading || page <= 1}>Previous</Btn>
            <span style={{ ...noteStyle, color: T.walnut }}>Page {page} of {lastPage}</span>
            <Btn size="sm" variant="subtle" onClick={() => setPage((p) => p + 1)} disabled={loading || page >= lastPage}>Next</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
